import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid
} from '@mui/material';
import { API_BASE_URL } from '../../config';
import secureStorage from '../../utils/secureStorage';
import { formatearFechaBD as formatFechaAR } from '../../utils/reciboUtils';
import NuevaSolicitud from './components/NuevaSolicitud';
import MisSolicitudes from './components/MisSolicitudes';
import Historial from './components/Historial';

function PanelReferente() {
  const [tabValue, setTabValue] = useState(0);
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [comentario, setComentario] = useState('');
  const [respondiendo, setRespondiendo] = useState(false);
  const [diasDisponibles, setDiasDisponibles] = useState(null);
  const [misSolicitudesData, setMisSolicitudesData] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const usuario_id = secureStorage.getItem('userId');

  useEffect(() => {
    cargarSolicitudes();
    cargarMisDatos();
  }, [refreshKey]);

  const cargarSolicitudes = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/vacaciones/pendientes-referente`, {
        credentials: 'include',  // Incluir cookies automáticamente
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) throw new Error('Error al cargar solicitudes');
      const data = await res.json();
      setSolicitudes(data);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cargarMisDatos = async () => {
    try {
      // Obtener días disponibles
      const diasRes = await fetch(`${API_BASE_URL}/api/vacaciones/dias-disponibles/${usuario_id}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (diasRes.ok) {
        const diasData = await diasRes.json();
        setDiasDisponibles(diasData[0] || null);
      }

      // Obtener mis solicitudes
      const solRes = await fetch(`${API_BASE_URL}/api/vacaciones/mis-solicitudes/${usuario_id}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (solRes.ok) {
        const solData = await solRes.json();
        setMisSolicitudesData(solData);
      }
    } catch (err) {
      console.error('Error al cargar mis datos:', err);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleAprobar = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setComentario('');
    setOpenDialog(true);
  };

  const handleRechazar = (solicitud) => {
    setSelectedSolicitud({ ...solicitud, rechazado: true });
    setComentario('');
    setOpenDialog(true);
  };

  const handleResponder = async (aprobado) => {
    try {
      setRespondiendo(true);
      const res = await fetch(
        `${API_BASE_URL}/api/vacaciones/responder-referente/${selectedSolicitud.id}`,
        {
          method: 'PUT',
          credentials: 'include',  // Incluir cookies automáticamente
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            aprobada: aprobado,
            comentarios: comentario
          })
        }
      );

      if (!res.ok) throw new Error('Error al responder solicitud');

      setOpenDialog(false);
      setComentario('');
      setSelectedSolicitud(null);
      cargarSolicitudes();
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setRespondiendo(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 'bold' }}>
        🏖️ Vacaciones - Panel de Referente
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* RESUMEN DÍAS - Solo para Mis Vacaciones */}
      {tabValue !== 0 && diasDisponibles && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography color="inherit" gutterBottom>
                  Disponibles 2025
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {diasDisponibles.dias_disponibles || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography color="inherit" gutterBottom>
                  Acumulados años anteriores
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {diasDisponibles.dias_acumulados_previos || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography color="inherit" gutterBottom>
                  Tomados
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {misSolicitudesData.filter(s => s.estado === 'aprobado').reduce((acc, s) => acc + s.dias_solicitados, 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography color="inherit" gutterBottom>
                  Total Disponible
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {(diasDisponibles.dias_disponibles || 0) + (diasDisponibles.dias_acumulados_previos || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* TABS */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Aprobar Solicitudes" icon="✅" iconPosition="start" />
          <Tab label="Solicitar Vacaciones" icon="✍️" iconPosition="start" />
          <Tab label="Mis Solicitudes" icon="📋" iconPosition="start" />
          <Tab label="Mi Historial" icon="📚" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* TAB 0: APROBAR SOLICITUDES DEL EQUIPO */}
      {tabValue === 0 && (
        <>
          {solicitudes.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="textSecondary">
                  ✓ No hay solicitudes pendientes de aprobación.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ background: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell><strong>Empleado</strong></TableCell>
                    <TableCell><strong>Referente</strong></TableCell>
                    <TableCell><strong>Período</strong></TableCell>
                    <TableCell align="center"><strong>Días</strong></TableCell>
                    <TableCell><strong>Comentarios</strong></TableCell>
                    <TableCell align="center"><strong>Acciones</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {solicitudes.map((solicitud) => (
                    <TableRow key={solicitud.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {solicitud.apellido}, {solicitud.nombre}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          DNI: {solicitud.dni} {solicitud.legajo ? `| Leg: ${solicitud.legajo}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {solicitud.referente_nombre && solicitud.referente_apellido 
                          ? `${solicitud.referente_apellido}, ${solicitud.referente_nombre}`
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        {formatFechaAR(solicitud.fecha_inicio)} -
                        {formatFechaAR(solicitud.fecha_fin)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={`${solicitud.dias_solicitados} días`} size="small" sx={{ mb: 0.5 }} />
                        <br />
                        {solicitud.dias_solicitados % 7 === 0 ? (
                          <Chip label="Aprobación final" size="small" color="success" variant="outlined" />
                        ) : (
                          <Chip label="Requiere RRHH" size="small" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200 }}>
                          {solicitud.comentarios_empleado || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleAprobar(solicitud)}
                          >
                            ✓ Aprobar
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={() => handleRechazar(solicitud)}
                          >
                            ✗ Rechazar
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* TAB 1: SOLICITAR MIS VACACIONES */}
      {tabValue === 1 && <NuevaSolicitud usuario_id={usuario_id} onSuccess={handleRefresh} />}

      {/* TAB 2: MIS SOLICITUDES */}
      {tabValue === 2 && <MisSolicitudes solicitudes={misSolicitudesData} />}

      {tabValue === 3 && <Historial usuario_id={usuario_id} />}

      {/* DIALOG PARA RESPONDER SOLICITUDES DEL EQUIPO */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedSolicitud?.rechazado ? '✗ Rechazar Solicitud' : '✓ Aprobar Solicitud'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedSolicitud && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="textSecondary">Empleado</Typography>
                <Typography variant="body1">{selectedSolicitud.usuario_nombre}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Período</Typography>
                <Typography variant="body1">
                  {formatFechaAR(selectedSolicitud.fecha_inicio)} a{' '}
                  {formatFechaAR(selectedSolicitud.fecha_fin)}
                </Typography>
              </Box>
              <TextField
                fullWidth
                label={selectedSolicitud.rechazado ? 'Motivo del rechazo' : 'Comentario (opcional)'}
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                multiline
                rows={3}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          {selectedSolicitud?.rechazado ? (
            <Button
              variant="contained"
              color="error"
              onClick={() => handleResponder(false)}
              disabled={respondiendo}
            >
              {respondiendo ? <CircularProgress size={24} /> : 'Rechazar'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={() => handleResponder(true)}
              disabled={respondiendo}
            >
              {respondiendo ? <CircularProgress size={24} /> : 'Aprobar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default PanelReferente;
